import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, LogOut } from "lucide-react";
import { LoadingBlock } from "@/components/AsyncState";
import { isVendorAuthenticated } from "@/lib/vendorAuth";
import { fetchMyVendorClaims, signOutVendor, type VendorClaim } from "@/lib/vendorPortal";

export const Route = createFileRoute("/vendor/pending")({
  head: () => ({
    meta: [
      { title: "Antrag wird geprüft · Venue Portal" },
      { name: "description", content: "Dein Venue-Antrag wird von unserem Team geprüft." },
    ],
  }),
  component: VendorPendingRoute,
});

type PendingState =
  | { state: "unauth" }
  | { state: "ok"; claims: VendorClaim[] };

function VendorPendingRoute() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<PendingState>({
    queryKey: ["vendorPendingState"],
    queryFn: async () => {
      if (!isVendorAuthenticated()) return { state: "unauth" };
      const claims = await fetchMyVendorClaims();
      return { state: "ok", claims };
    },
    staleTime: 0,
  });

  useEffect(() => {
    if (!data) return;
    if (data.state === "unauth") {
      void navigate({ to: "/vendor/login", replace: true });
      return;
    }
    if (data.claims.some((c) => c.status === "approved")) {
      void navigate({ to: "/vendor/dashboard", replace: true });
      return;
    }
    if (data.claims.length === 0) {
      void navigate({ to: "/vendor/register-venue", replace: true });
    }
  }, [data, navigate]);

  const pendingClaims = data?.state === "ok" ? data.claims.filter((c) => c.status === "pending") : [];

  const handleLogout = () => {
    void signOutVendor().finally(() => {
      void navigate({ to: "/vendor/login", replace: true });
    });
  };

  if (isLoading || !data || data.state !== "ok" || pendingClaims.length === 0) {
    return (
      <div className="min-h-screen px-5 pb-12 pt-14">
        <LoadingBlock label="Antragsstatus laden…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-5 pb-12 pt-14">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-glow" />

      <div className="relative mx-auto max-w-md text-center">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-500/15 text-amber-400">
          <Clock className="h-7 w-7" />
        </span>
        <h1 className="mt-4 text-2xl font-bold">Dein Antrag wird geprüft</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Danke für deine Registrierung! Unser Team prüft deinen Venue-Antrag und schaltet dich
          frei, sobald alles verifiziert ist. Du erhältst eine Benachrichtigung, sobald es
          losgeht.
        </p>

        <div className="mt-6 space-y-2 text-left">
          {pendingClaims.map((claim) => (
            <div
              key={claim.venueId}
              className="flex items-center justify-between rounded-2xl border border-border bg-surface-elevated px-4 py-3"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">
                  {claim.venueName ?? "Deine Venue"}
                </span>
                <span className="block text-xs text-muted-foreground">Rolle: {claim.role}</span>
              </span>
              <span className="shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400">
                In Prüfung
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          <LogOut className="h-3.5 w-3.5" />
          Abmelden
        </button>
      </div>
    </div>
  );
}
